import { createSelector } from '@reduxjs/toolkit';
import { decode } from 'html-entities';
import codes from '../utils/constants/codes';

const questionProjections = {};

questionProjections.questionsExists = createSelector(
    state => state.question.question.byId,
    questionsById => {
        return Object.keys(questionsById).length > 0;
    },
);

questionProjections.questionHelperText = (questionCode, formAnswers) =>
    createSelector(
        state => state.question.question.byId[questionCode],
        state => state.question.question.byId,
        state => state.questionType.questionType.byId,
        (givenQuestion, questionsById, questionTypesById) => {
            if (!givenQuestion) {
                return null;
            }

            // logic for question with options - dropdown
            const formAnswer = formAnswers
                ? formAnswers[givenQuestion.code]
                : null;
            if (
                questionTypesById[givenQuestion.questionTypeId] &&
                questionTypesById[givenQuestion.questionTypeId].code === 'list'
            ) {
                const answerOfGivenQuestion = formAnswer
                    ? givenQuestion.options.find(o => o.id === formAnswer)
                    : null;
                if (
                    answerOfGivenQuestion &&
                    answerOfGivenQuestion.labelDisplayedWhenAnswered
                ) {
                    return answerOfGivenQuestion.labelDisplayedWhenAnswered;
                }
            } else {
                if (formAnswer && givenQuestion.labelDisplayedWhenAnswered) {
                    return givenQuestion.labelDisplayedWhenAnswered;
                }
            }

            return null;
        },
    );

questionProjections.attachmentsQuestionDocumentTypes = (
    attachmentQuestionCode,
    asnwerValues,
) =>
    createSelector(
        state => state.question.question.byId[attachmentQuestionCode],
        question => {
            if (!question || !question.documentTypes) {
                return [];
            }
            return question.documentTypes.filter(
                type =>
                    type.isSuggested &&
                    (!type.enabledByOptionId ||
                        (type.enabledByOptionId &&
                            asnwerValues.includes(type.enabledByOptionId) ===
                                false)),
            );
        },
    );

questionProjections.fitlerAnswersByQuestions = answers =>
    createSelector(
        state => state.question.question.byId,
        state => state.documentType.documentType.byId,
        state => state.question.loading,
        (questions, documentsTypes, loadingQuestions) => {
            const arrayOfDocumentTypes = Object.values(documentsTypes);
            const arrayOfQuestions = Object.keys(questions);
            if (answers.length === 0 || arrayOfQuestions.length === 0) {
                return {};
            }
            let objOfAnswers = {};
            if (answers && answers.length > 0) {
                objOfAnswers = answers.reduce((obj, cur, i) => {
                    if (cur.attachments && cur.attachments.length > 0) {
                        let mappedAttachments = cur.attachments.map(
                            attachment => {
                                let documentTypesOfAttachment = [];
                                if (arrayOfDocumentTypes.length > 0) {
                                    attachment.documentTypeCodes.forEach(
                                        type => {
                                            let document =
                                                arrayOfDocumentTypes.find(
                                                    item => item.code === type,
                                                );
                                            if (
                                                document &&
                                                Object.keys(document).length !==
                                                    0
                                            ) {
                                                documentTypesOfAttachment.push({
                                                    item: { ...document },
                                                    label: document.name,
                                                    value: document.code,
                                                });
                                            }
                                        },
                                    );
                                }

                                return {
                                    name: attachment.fullFileName
                                        ? attachment.fullFileName
                                        : '',
                                    id: attachment.id ? attachment.id : '',
                                    documentTypes: documentTypesOfAttachment,
                                };
                            },
                        );
                        return {
                            ...obj,
                            [cur.questionCode]: mappedAttachments,
                        };
                    }
                    if (cur.flag) {
                        return {
                            ...obj,
                            [cur.questionCode]: ['on'],
                        };
                    }
                    if (cur.date) {
                        return {
                            ...obj,
                            [cur.questionCode]: new Date(cur.date),
                        };
                    }
                    let answers = [cur.text, cur.amount, cur.optionId];
                    let answer = answers.find(item => item !== null);
                    if (answer) {
                        return {
                            ...obj,
                            [cur.questionCode]: cur.text
                                ? decode(answer, { scope: 'strict' })
                                : answer,
                        };
                    } else {
                        return {
                            ...obj,
                        };
                    }
                }, {});
            }

            return objOfAnswers;
        },
    );

questionProjections.questionListOfGroupStep = questionGroupStepCode =>
    createSelector(
        state => state.question.question.byId,
        state =>
            state.questionGroupStep.questionGroupStep.byId[
                questionGroupStepCode
            ],
        (questionById, groupStep) => {
            if (!groupStep) {
                return [];
            }

            return Object.entries(questionById)
                .filter(
                    ([key, value]) =>
                        value.questionGroupStepId === groupStep.id,
                )
                .map(([ley, value]) => ({
                    ...value,
                    questionValidationRules: value.questionValidationRules
                        ? value.questionValidationRules
                        : {},
                }));
        },
    );

questionProjections.getAllRelatedQuestions = postcodeLoader =>
    createSelector(
        state => state.question.question.byId,
        questionsById => {
            const questionValues = Object.values(questionsById);
            let questionsWithRelatedQuestions = [];
            if (questionValues.length > 0) {
                questionValues.forEach(question => {
                    let relatedQuestions = question.relatedQuestions
                        ? Object.values(question.relatedQuestions)
                        : [];
                    if (relatedQuestions.length > 0) {
                        let questionWithRelatedQuestions = {
                            code: question.code,
                            questions: relatedQuestions,
                        };
                        questionsWithRelatedQuestions.push(
                            questionWithRelatedQuestions,
                        );
                    }
                });
                return questionsWithRelatedQuestions
                    ? questionsWithRelatedQuestions.find(
                          question => question.code === postcodeLoader,
                      )
                    : null;
            } else {
                return null;
            }
        },
    );

questionProjections.filterQuestionsByGroup = groupId =>
    createSelector(
        state => state.question.question.byId,
        questionsById => {
            return Object.values(questionsById).filter(
                q => q.groupId === groupId,
            );
        },
    );

questionProjections.filterQuestionsByGroupCode = groupCode =>
    createSelector(
        state => state.question.question.byId,
        questionsByCode => {
            return Object.values(questionsByCode).filter(
                q => q.groupCode === groupCode,
            );
        },
    );

questionProjections.areQuestionsByGroupExist = groupId =>
    createSelector(
        state => state.question.question.byId,
        questionsById => {
            return (
                Object.values(questionsById).filter(q => q.groupId === groupId)
                    .length === 0
            );
        },
    );

questionProjections.areQuestionsByGroupCodeExist = groupCode =>
    createSelector(
        state => state.question.question.byId,
        questionsByCode => {
            return (
                Object.values(questionsByCode).filter(
                    q => q.groupCode === groupCode,
                ).length === 0
            );
        },
    );

questionProjections.surveyQuestions = groupId =>
    createSelector(
        state => state.question.question.byId,
        state => state.questionType.questionType.byId,
        state => state.question.loading,
        state => state.questionValidationRule.questionValidationRule,
        state => state.question.question.allIds,
        (
            questionsById,
            questionTypeById,
            questionTypesLoading,
            questionValidationRules,
            questionsAllIds,
        ) => {
            if (!questionsById || groupId === null) {
                return {
                    questions: [],
                    questionTypes: {},
                    questionsById: {},
                    questionTypesLoading: false,
                    questionValidationRules: {},
                    questionsAllIds: [],
                };
            }
            let questions = Object.values(questionsById)
                .filter(q => q.groupId === groupId)
                .map((item, index, arr) => {
                    return {
                        ...item,
                        addHorizontalLine:
                            item.section !== arr[index + 1]?.section &&
                            index !== arr.length - 1,
                    };
                });
            return {
                questions: questions.sort((a, b) => a.rank - b.rank),
                questionTypes: questionTypeById,
                questionsById: questionsById,
                questionTypesLoading: questionTypesLoading,
                questionValidationRules: questionValidationRules,
                questionsAllIds: questionsAllIds,
            };
        },
    );

questionProjections.questionsWithGroupId = groupId =>
    createSelector(
        state => state.question.question.byId,
        state => state.questionType.questionType.byId,
        state => state.question.loading,
        state => state.questionValidationRule.questionValidationRule,
        state => state.question.question.allIds,
        (
            questionsByGroupId,
            questionTypeById,
            questionTypesLoading,
            questionValidationRules,
            questionsAllIds,
        ) => {
            if (
                !questionsByGroupId ||
                !questionTypeById ||
                !questionValidationRules
            ) {
                return {
                    questions: [],
                    questionsById: {},
                    questionTypes: {},
                    questionValidationRules: {},
                    questionTypesLoading: questionTypesLoading,
                };
            }

            return {
                questions: Object.values(questionsByGroupId)
                    .filter(q => q?.groupId === groupId)
                    .map((question, index, arr) => {
                        if (
                            question?.questionTypeId ===
                                codes.questionTypeIdForCheckboxes &&
                            !question.isReadonly
                        ) {
                            return { ...question, groupShape: 'reverse' };
                        } else {
                            return question;
                        }
                    })
                    .sort((a, b) => a.rank - b.rank),
                questionTypes: questionTypeById,
                questionsById: questionsByGroupId,
                questionTypesLoading: questionTypesLoading,
                questionValidationRules: questionValidationRules,
                questionsAllIds: questionsAllIds,
            };
        },
    );

export default questionProjections;
